import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import AdminSidebar from './AdminSidebar';
import { 
  FiMenu,
  FiX
} from 'react-icons/fi';
import { useAuth } from '../../../hooks/useAuth';
import { useAdmin } from '../../../hooks/useAdmin';
import { Avatar } from '../../common';

const AdminLayout: React.FC = () => {
  const { user } = useAuth();
  const { pendingCount, loadDashboardStats } = useAdmin();
  const location = useLocation();
  const [pageTitle, setPageTitle] = useState('Admin Dashboard');
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Load dashboard stats
  useEffect(() => {
    loadDashboardStats();
    
    // Refresh every 30 seconds
    const interval = setInterval(() => {
      loadDashboardStats();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [loadDashboardStats]);

  // Set page title based on current route
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/admin/dashboard')) setPageTitle('Admin Dashboard');
    else if (path.includes('/admin/meetings')) setPageTitle('Meeting Approval');
    else if (path.includes('/admin/users')) setPageTitle('User Management');
    else setPageTitle('Admin Panel');
  }, [location]);

  // Close sidebar when route changes on mobile
  useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [location]);

  return (
    <div className="w-full h-screen flex overflow-hidden">
      {/* Background decorative elements */}
      <div className="fixed inset-0 -z-10 bg-background-dark">
        <div className="absolute left-1/4 top-1/4 w-96 h-96 bg-primary-500/20 rounded-full filter blur-[100px]" />
        <div className="absolute right-1/4 bottom-1/4 w-96 h-96 bg-secondary-500/20 rounded-full filter blur-[100px]" />
      </div>
      
      {/* Mobile sidebar backdrop */}
      {isMobileSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar - static on desktop, custom mobile implementation */}
      <div className="hidden lg:block w-64 h-full">
        <AdminSidebar pendingCount={pendingCount} />
      </div>
      
      {/* Mobile Sidebar - Only renders when open */}
      {isMobileSidebarOpen && (
        <div className="fixed inset-y-0 left-0 z-40 w-64 bg-background-dark lg:hidden">
          <div className="h-full flex flex-col">
            <div className="py-6 px-4 border-b border-white/10 flex justify-between items-center">
              <span className="text-2xl font-bold bg-gradient-to-r from-primary-400 to-secondary-400 bg-clip-text text-transparent">
                Admin Panel
              </span>
              <button 
                onClick={() => setIsMobileSidebarOpen(false)}
                className="text-white/70 p-1.5 rounded-lg hover:bg-white/10"
              >
                <FiX className="h-6 w-6" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              <AdminSidebar pendingCount={pendingCount} isMobile />
            </div>
          </div>
        </div>
      )}
      
      {/* Main Content - full width */}
      <div className="w-full lg:w-[calc(100%-16rem)] flex flex-col overflow-hidden">
        {/* Header */}
        <header className="flex justify-between items-center px-6 py-4 bg-background-glass backdrop-blur-sm border-b border-white/5 relative z-10">
          {/* Mobile Menu Button and Title */}
          <div className="flex items-center">
            <button 
              className="lg:hidden mr-3 p-2 rounded-full hover:bg-white/5 transition-colors"
              onClick={() => setIsMobileSidebarOpen(true)}
              aria-label="Toggle menu"
            >
              <FiMenu className="text-white/80 text-xl" />
            </button>
            <h1 className="text-xl font-semibold text-white">
              {pageTitle}
              {pendingCount > 0 && pageTitle !== 'Meeting Approval' && (
                <span className="ml-2 text-sm bg-primary-500 text-white px-2 py-0.5 rounded-full">
                  {pendingCount} pending
                </span>
              )}
            </h1>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* User Avatar */}
            <div className="flex items-center">
              <Avatar 
                src={user?.profilePicture} 
                name={user?.name}
                size="sm" 
                status="online"
              />
              <span className="ml-3 text-white hidden md:block">{user?.name}</span>
            </div>
          </div>
        </header>
        
        {/* Page Content with decorative elements */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden p-6 relative">
          {/* Additional decorative elements */}
          <div className="absolute -top-24 -right-24 w-48 h-48 bg-primary-500/10 rounded-full" />
          <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-secondary-500/10 rounded-full" />
          
          {/* Page content */}
          <div className="relative z-10 max-w-full">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
